import React from 'react';
import { Box, Typography, CircularProgress, Alert, Button, Chip } from '@mui/material';
import {
  AttachFile as AttachFileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Description as WordIcon,
  TableChart as ExcelIcon,
  Email as MsgIcon,
  AccessTime as AccessTimeIcon,
  PersonOutline as PersonIcon,
} from '@mui/icons-material';
import { colors, fonts, spacing, borderRadius, shadows } from '../../../constants';
import { isImageFile, isPdfFile, isWordFile, isExcelFile, isMsgFile, getFileName } from '../../../utils/fileUtils';

/**
 * Format a date for the timeline display (DD/MM/YYYY HH:MM:SS)
 */
const formatTimelineDate = (date) => {
  if (!date) return 'N/A';
  try {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  } catch {
    return 'N/A';
  }
};

/**
 * Humanize a status string (e.g., "status_changed" -> "Status Changed")
 */
const humanizeStatus = (value) => {
  if (!value) return '';
  return value.toString().replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

/**
 * Get badge color configuration based on action type
 */
const getActionBadgeConfig = (action) => {
  const configs = {
    created: {
      label: 'Created',
      bg: 'rgba(16, 185, 129, 0.14)',
      color: '#047857',
      borderColor: 'rgba(16, 185, 129, 0.28)',
      iconBg: 'rgba(16, 185, 129, 0.18)',
      iconColor: '#047857',
    },
    assigned: {
      label: 'Assignment',
      bg: 'rgba(59, 130, 246, 0.14)',
      color: '#1D4ED8',
      borderColor: 'rgba(59, 130, 246, 0.26)',
      iconBg: 'rgba(59, 130, 246, 0.18)',
      iconColor: '#1D4ED8',
    },
    delegated: {
      label: 'Delegated',
      bg: 'rgba(245, 158, 11, 0.16)',
      color: '#B45309',
      borderColor: 'rgba(245, 158, 11, 0.28)',
      iconBg: 'rgba(245, 158, 11, 0.2)',
      iconColor: '#B45309',
    },
    escalated: {
      label: 'Escalation',
      bg: 'rgba(239, 68, 68, 0.14)',
      color: '#B91C1C',
      borderColor: 'rgba(239, 68, 68, 0.3)',
      iconBg: 'rgba(239, 68, 68, 0.18)',
      iconColor: '#B91C1C',
    },
    responded: {
      label: 'Response',
      bg: 'rgba(35, 52, 145, 0.14)',
      color: colors.primary,
      borderColor: 'rgba(35, 52, 145, 0.26)',
      iconBg: 'rgba(35, 52, 145, 0.16)',
      iconColor: colors.primary,
    },
    closed: {
      label: 'Closed',
      bg: 'rgba(76, 91, 173, 0.16)',
      color: colors.primaryDark,
      borderColor: 'rgba(76, 91, 173, 0.28)',
      iconBg: 'rgba(76, 91, 173, 0.2)',
      iconColor: colors.primaryDark,
    },
    reopened: {
      label: 'Reopened',
      bg: 'rgba(14, 165, 233, 0.14)',
      color: '#0369A1',
      borderColor: 'rgba(14, 165, 233, 0.28)',
      iconBg: 'rgba(14, 165, 233, 0.2)',
      iconColor: '#0369A1',
    },
    status_changed: {
      label: 'Status Update',
      bg: 'rgba(30, 64, 175, 0.14)',
      color: '#1E40AF',
      borderColor: 'rgba(30, 64, 175, 0.24)',
      iconBg: 'rgba(30, 64, 175, 0.18)',
      iconColor: '#1E40AF',
    },
    deleted: {
      label: 'Deleted',
      bg: 'rgba(220, 38, 38, 0.14)',
      color: '#991B1B',
      borderColor: 'rgba(220, 38, 38, 0.28)',
      iconBg: 'rgba(220, 38, 38, 0.18)',
      iconColor: '#991B1B',
    },
  };

  return configs[action] || {
    label: humanizeStatus(action) || 'Activity',
    bg: 'rgba(100, 116, 139, 0.14)',
    color: '#475569',
    borderColor: 'rgba(100, 116, 139, 0.24)',
    iconBg: 'rgba(100, 116, 139, 0.18)',
    iconColor: '#475569',
  };
};

/**
 * ActivityLogCard Component
 * Renders a single flat activity log card matching Advik Activity Logs design.
 */
const ActivityLogCard = ({ event, handleAttachmentClick }) => {
  const actorName =
    event.performedByName ||
    event.performedBy?.full_name ||
    event.performedBy?.name ||
    'System';
  const eventTime = formatTimelineDate(event.performedAt);
  const eventMessage = event.message || event.description || 'Activity';
  const hasAttachments = Array.isArray(event.attachments) && event.attachments.length > 0;
  const badgeConfig = getActionBadgeConfig(event.action);

  return (
    <Box
      sx={{
        p: { xs: spacing.sm, sm: '14px 16px' },
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        borderRadius: borderRadius.md,
        border: `1px solid ${colors.borderDefault}`,
        boxShadow: shadows.md,
        transition: 'all 0.2s ease',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: '3px',
          background: `linear-gradient(180deg, ${badgeConfig.borderColor} 0%, transparent 100%)`,
        },
        '&:hover': {
          boxShadow: shadows.lg,
          transform: 'translateY(-1px)',
        },
      }}
    >
      {/* Top Row: Action Badge (left) + Timestamp (right) */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: '8px',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Box
            sx={{
              width: 26,
              height: 26,
              borderRadius: '50%',
              backgroundColor: badgeConfig.iconBg,
              color: badgeConfig.iconColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <PersonIcon sx={{ fontSize: '16px' }} />
          </Box>
          <Chip
            label={badgeConfig.label}
            size="small"
            sx={{
              height: '23px',
              fontSize: fonts.fontSizeSm,
              fontWeight: fonts.weightSemiBold,
              backgroundColor: badgeConfig.bg,
              color: badgeConfig.color,
              border: `1px solid ${badgeConfig.borderColor}`,
              borderRadius: '6px',
              '& .MuiChip-label': {
                px: '7px',
              },
            }}
          />
        </Box>
        <Typography
          variant="caption"
          sx={{
            color: colors.textSecondary,
            fontSize: fonts.fontSizeSm,
            fontWeight: fonts.weightMedium,
            whiteSpace: 'nowrap',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <AccessTimeIcon sx={{ fontSize: '14px' }} />
          {eventTime}
        </Typography>
      </Box>

      {/* Middle: Activity message */}
      <Typography
        variant="body2"
        sx={{
          color: colors.textPrimaryDark,
          fontSize: fonts.fontSizeMd,
          fontWeight: fonts.weightNormal,
          lineHeight: fonts.lineHeightNormal,
          mb: spacing.xs,
        }}
      >
        {eventMessage}
      </Typography>

      {/* Attachments (if any) */}
      {hasAttachments && (
        <Box sx={{ mb: '6px' }}>
          <Typography
            variant="caption"
            sx={{
              color: colors.textSecondary,
              display: 'block',
              mb: '4px',
              fontWeight: fonts.weightSemiBold,
              fontSize: fonts.fontSizeXs,
            }}
          >
            Attachments
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {event.attachments.map((file, fileIndex) => (
              <Button
                key={file._id || fileIndex}
                onClick={() => handleAttachmentClick(file)}
                variant="outlined"
                size="small"
                startIcon={
                  isImageFile(file) ? (
                    <ImageIcon sx={{ fontSize: '0.85rem' }} />
                  ) : isPdfFile(file) ? (
                    <PdfIcon sx={{ fontSize: '0.85rem' }} />
                  ) : isWordFile(file) ? (
                    <WordIcon sx={{ fontSize: '0.85rem' }} />
                  ) : isExcelFile(file) ? (
                    <ExcelIcon sx={{ fontSize: '0.85rem' }} />
                  ) : isMsgFile(file) ? (
                    <MsgIcon sx={{ fontSize: '0.85rem' }} />
                  ) : (
                    <AttachFileIcon sx={{ fontSize: '0.85rem' }} />
                  )
                }
                sx={{
                  textTransform: 'none',
                  borderRadius: borderRadius.sm,
                  fontSize: fonts.fontSizeXs,
                  padding: '2px 8px',
                  cursor: 'pointer',
                  borderColor: colors.borderDefault,
                  color: colors.textSecondary,
                  backgroundColor: colors.backgroundWhite,
                  '&:hover': {
                    backgroundColor: colors.primaryTint,
                    borderColor: colors.primaryDark,
                  },
                }}
              >
                {getFileName(file)}
              </Button>
            ))}
          </Box>
        </Box>
      )}

      {/* Bottom Right: "By: <actor>" */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          pt: '2px',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: colors.textTertiary,
            fontSize: fonts.fontSizeSm,
            fontStyle: 'italic',
            fontWeight: fonts.weightMedium,
          }}
        >
          By: {actorName}
        </Typography>
      </Box>
    </Box>
  );
};

/**
 * ActivityTimeline Component
 * Renders activity log cards in a flat layout (matching Advik Activity Logs design).
 *
 * @param {Array} timelineEvents - Timeline events
 * @param {Object} timelineCounts - Timeline counts
 * @param {boolean} timelineLoading - Whether timeline is loading
 * @param {string} timelineError - Timeline error message
 * @param {function} handleAttachmentClick - Attachment click handler
 */
const ActivityTimeline = ({
  timelineEvents,
  timelineCounts,
  timelineLoading,
  timelineError,
  handleAttachmentClick,
}) => {
  const hasEvents = timelineEvents.length > 0;
  const activityCount = timelineCounts?.total || timelineEvents.length;

  if (timelineLoading && !hasEvents) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: spacing.sm }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (timelineError) {
    return (
      <Alert
        severity="warning"
        sx={{
          fontSize: fonts.fontSizeSm,
          backgroundColor: 'rgba(251, 146, 60, 0.12)',
          color: colors.primaryDark,
          fontWeight: fonts.weightMedium,
          '& .MuiAlert-icon': {
            color: '#f97316',
          },
          '& .MuiAlert-message': {
            color: colors.primaryDark,
          },
        }}
      >
        {timelineError}
      </Alert>
    );
  }

  if (!hasEvents) {
    return (
      <Box
        sx={{
          p: spacing.sm,
          borderRadius: borderRadius.input,
          border: `1px dashed ${colors.borderDefault}`,
          background: colors.primarySoft,
          textAlign: 'center',
          color: colors.primaryDark,
          fontSize: fonts.fontSizeSm,
          fontWeight: fonts.weightMedium,
        }}
      >
        Timeline data is not available yet. Activity will appear here as the query progresses.
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
      {timelineEvents.map((event, index) => {
        const key = `${event.type}-${event.action}-${event.performedAt || index}-${index}`;
        return (
          <ActivityLogCard
            key={key}
            event={event}
            handleAttachmentClick={handleAttachmentClick}
          />
        );
      })}
    </Box>
  );
};

export default ActivityTimeline;
